<template>
  <div class="llm-config-page">
    <!-- 顶栏与下方主栏+预览同宽、整体居中，避免顶栏全宽而中间留白 -->
    <div class="llm-layout-shell">
    <!-- 顶部栏（与原型：左标题/编号/状态/时间，右 历史版本 / 发布 / 保存） -->
    <div class="page-header">
      <div class="header-left">
        <div class="header-left-inner">
          <h1 class="page-title">{{ llmTitle || '—' }}</h1>
          <span v-if="sceneId" class="header-scene-code">{{ headerSceneCode }}</span>
          <span
            v-if="releaseText"
            class="header-status-pill"
            :class="{ 'is-published': storeSceneRelease === 1 }"
          >{{ releaseText }}</span>
          <span class="header-meta">创建：{{ createTime || '—' }}</span>
          <span class="header-meta">更新：{{ updateTimeStr }}</span>
        </div>
      </div>
      <div class="header-actions">
        <LlmHistoryVersions
          :scene-id="sceneId"
          @restore="applyLlmFromHistoryRestore"
        />
        <Button class="header-btn-publish" @click="onClickPublish">发布</Button>
        <Button class="header-btn-save" @click="handleSave">保存</Button>
      </div>
    </div>

      <!-- 主内容区：左侧 Tab + 右侧模拟对话（话术调试器能力） -->
    <div class="page-body-split">
      <div class="page-body-main">
        <div class="page-body-main-inner">
          <!-- View Design：嵌套 Tabs 时外层须设置 name，各 TabPane 须 tab 指向该 name，否则内层 TabPane 会被外层一并收集进主导航 -->
          <Tabs v-model="activeMainTab" class="llm-main-tabs" name="llmMain" :animated="false">
            <TabPane label="提示词配置" name="prompt" tab="llmMain">
              <div class="llm-tab-pane-scroll">
                <LlmPromptConfigTab
                  :form="form"
                  :prompt-mode.sync="promptMode"
                  :flow-section-order.sync="flowSectionOrder"
                  :custom-flow-sections="customFlowSections"
                  :flow-section-titles="flowSectionTitles"
                  :expanded="expanded"
                  :flow-guide-expanded="flowGuideExpanded"
                  :fixed-title-show-input="fixedTitleShowInput"
                  :custom-title-show-input="customTitleShowInput"
                  :intent-tag="intentTag"
                  :get-role-info-text-for-prompt="getRoleInfoTextForPrompt"
                  :memory-enabled="voiceCall.callControl.memory_enabled === true"
                  :is-third-llm="isThirdLlm"
                />
              </div>
            </TabPane>

            <TabPane label="模型与语音" name="voice" tab="llmMain">
              <div class="llm-tab-pane-scroll">
                <LlmVoiceCallTab
                  :voice-call="voiceCall"
                  :is-third-llm="isThirdLlm"
                  @clone-voice-saved="handleSave"
                />
              </div>
            </TabPane>

            <TabPane label="意向与标签" name="intent" tab="llmMain">
              <div class="llm-tab-pane-scroll">
                <LlmIntentionTagTab
                  :intent-tag="intentTag"
                  :scene-name="storeSceneName"
                  :is-third-llm="isThirdLlm"
                />
              </div>
            </TabPane>

            <TabPane label="通话控制" name="callControl" tab="llmMain">
              <div class="llm-tab-pane-scroll">
                <LlmCallControlTab
                  :call-control="voiceCall.callControl"
                  :preset-mode="voiceCall.presetMode"
                  :g2-locked="isG2Mode"
                  :supreme-locked="isSupremeService"
                  :scene-id="sceneId"
                  :is-third-llm="isThirdLlm"
                  :is-admin-role="isAdminRole"
                  :is-flagship="isFlagshipService"
                  :customer-info-params="form.customerInfoParams"
                  :little-assistant="form.littleAssistant"
                  :external-apis-json="form.externalApisJson"
                  :external-apis-form="form.externalApisForm"
                  :event-triggers="form.eventTriggers"
                  :intent-tag="intentTag"
                />
              </div>
            </TabPane>
          </Tabs>
        </div>
      </div>
      <aside class="page-body-aside">
        <LlmVoiceTest :voice-call="voiceCall" :opening-text="form.opening" />
      </aside>
    </div>
    </div>

    <Modal
      v-model="publishModalVisible"
      title="发布"
      class-name="llm-publish-modal"
      :footer-hide="true"
      width="420"
    >
      <p class="publish-modal-tip">是否应用于进行中的任务？</p>
      <div class="publish-modal-btns">
        <Button @click="publishModalVisible = false">取消</Button>
        <Button
          class="header-btn-publish"
          :loading="publishLoading"
          @click="doPublish(false)"
        >只发布</Button>
        <Button
          type="primary"
          :loading="publishLoading"
          @click="doPublish(true)"
        >发布并应用所有任务</Button>
      </div>
    </Modal>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import {
  publishSceneA,
  addCrmFormA,
  updateCrmFormA,
  getCrmFormA,
  getCrmFormListPageA,
  addCrmFormSceneA,
  updateCrmFormSceneA,
  getCrmFormSceneListA,
  getUserInfoA,
  getSystemFeeForHuashuEditA,
} from '@/server/api'
import LlmVoiceTest from './components/voice-test/index.vue'
import LlmHistoryVersions from './components/history-versions/index.vue'
import LlmPromptConfigTab from './components/prompt-config/index.vue'
import LlmIntentionTagTab from './components/intention-tag/index.vue'
import LlmVoiceCallTab from './components/voice-call/index.vue'
import LlmCallControlTab from './components/call-control/index.vue'
import {
  DEFAULT_PROMPT_SECTION_ORDER,
  PROMPT_MODE,
  ROLE_PERSONA_SECTION_KEY,
} from './constants/flowSection'
import {
  applyBusinessFlowFromLlmsettings,
  applyCustomerInfoParamsFromSnapshot,
  applyLittleAssistantFromSettings,
  applySpeechParamsFromParameterConfig,
  buildCustomerInfoParamsSnapshot,
  buildLittleAssistantSnapshot,
  buildParameterConfigSnapshot,
  createDefaultCustomerInfoParams,
  createDefaultLittleAssistant,
  createDefaultSpeechParams,
  getCustomRow,
  getFixedSectionTitle,
  getFlowSectionPromptValue,
  isCustomFlowKey,
  normalizePromptSectionOrder,
  snapshotPromptMode,
} from './utils/flowSectionHelpers'
import {
  buildTabbedLlmsettingsDocument,
  parseLlmsettingsV2,
  validateLlmsettingsSchema,
} from './utils/llmsettingsSchema'
import { coerceLlmsettingsForUi } from './utils/llmsettingsMigrate'
import {
  VOICE_ENGINE,
  getPresetCatalogEntry,
  resolveVoiceEngine,
  getDefaultPresetId,
  getCloneVendor,
  findEngineCloneVoiceById,
  isPresetCatalogVoiceId,
  getStaleVoiceAuditionMessage,
  TRIPLE_CLONE_VENDOR,
} from './constants/voicePresets'
import {
  PRESET_MODE,
  SERVICE_VERSION,
  createDefaultTripleAccess,
  createDefaultFlagshipAccess,
  buildTripleAccessSnapshot,
  buildFlagshipAccessSnapshot,
  applyTripleAccessToForm,
  restoreFlagshipAccess,
  resolveVoicePresetModeFromSettings,
  resolveServiceVersion,
  resolveServiceVersionWithFee,
  normalizeHuashuFeeVersions,
  resolveSelectedLlmModelName,
  isG2TripleAccessMode,
} from './utils/tripleAccessHelpers'
import {
  applyCallControlToForm,
  buildCallControlSnapshot,
  createDefaultCallControl,
} from './utils/callControlHelpers'
import {
  applyExternalApisGroupFromSnapshot,
  buildExternalApisGroupSnapshot,
} from './utils/externalApiHelpers'
import {
  applyEventTriggersFromSnapshot,
  buildEventTriggersSnapshot,
} from './utils/eventTriggerHelpers'
import {
  applyIntentTagFromSnapshot,
  buildIntentTagSnapshot,
  buildCrmFormPayload,
  createDefaultIntentTagState,
  findCrmFormByNameFromListResponse,
  hasForcedExtractFormData,
  mergeCrmFormUpdatePayload,
  parseCrmFormIdFromResponse,
  parseCrmFormSceneListFromResponse,
  toCrmFormIdNumber,
} from './utils/intentTagHelpers'
import { INTENT_TAG_MODE } from './constants/intentTag'

/** sceneJson 可 JSON 序列化；避免再 import lodash/cloneDeep 与别处重名 */
function cloneSceneJson(obj) {
  if (obj == null || typeof obj !== 'object') return obj
  try {
    return JSON.parse(JSON.stringify(obj))
  } catch (e) {
    return { ...obj }
  }
}

/** 大模型话术保存/发布时写入 sceneJson 内 llm-omini.trunk_ip；优先 manualIp，否则按域名选测/正式 */
const LLM_SCENE_TRUNK_CONFIG = {
  manualIp: '',
  testIp: '172.17.98.104:13000',
  prodIp: '172.17.98.109:13000',
}

function isLlmTrunkTestHostname() {
  if (typeof window === 'undefined') return false
  const h = String(window.location.hostname || '').toLowerCase()
  return h.includes('bj') || h.includes('localhost')
}

/** 大模型页面写入的 thirdllm 节点标记。流程里的 LLM Pro 没有这个字段。 */
const LLM_NODE_SOURCE = 'llm'

function applyLlmNodeSource(node, isOurLlm) {
  if (!node || typeof node !== 'object') return node
  if (isOurLlm) node.llm_source = LLM_NODE_SOURCE
  else delete node.llm_source
  return node
}

function isOurLlmThirdNode(node) {
  return !!(node && String(node.llm_source || '').trim() === LLM_NODE_SOURCE)
}

function savedVoiceSettings(llmsettings) {
  const src = llmsettings && typeof llmsettings === 'object' ? llmsettings : {}
  const vs = src.voice_settings
  return vs && typeof vs === 'object' ? vs : {}
}

/** type 为 thirdllm，且不是大模型页面写入的节点 */
function isExternalThirdLlmNode(node, llmsettings) {
  if (!node || node.type !== 'thirdllm') return false
  if (isOurLlmThirdNode(node)) return false
  const vs = savedVoiceSettings(llmsettings)
  if (String(vs.service_version || '').trim() === 'flagship') return false
  if (vs.flagship_access && vs.flagship_access.selected === true) return false
  return true
}

function pickLlmTrunkIp(config = LLM_SCENE_TRUNK_CONFIG) {
  const manual = config.manualIp != null ? String(config.manualIp).trim() : ''
  if (manual) return manual
  return isLlmTrunkTestHostname()
    ? String(config.testIp || '').trim()
    : String(config.prodIp || '').trim()
}

export default {
  name: 'LlmConfig',
  components: {
    LlmVoiceTest,
    LlmHistoryVersions,
    LlmPromptConfigTab,
    LlmIntentionTagTab,
    LlmVoiceCallTab,
    LlmCallControlTab,
  },
  data() {
    return {
      activeMainTab: 'prompt',
      promptMode: PROMPT_MODE.FREE,
      llmTitle: '',
      releaseText: '',
      sceneId: 0,
      createTime: '',
      form: {
        roleName: '',
        identities: [],
        communicationStyles: [],
        roleDesc: '',
        customPrompt: '',
        customTools: '',
        externalApisJson: [],
        externalApisForm: [],
        eventTriggers: [],
        littleAssistant: createDefaultLittleAssistant(),
        customerInfoParams: createDefaultCustomerInfoParams(),
        businessBackground: '',
        businessTarget: '',
        businessProcess: '',
        opening: '',
        dialogueExample: '',
        dialogueConstraint: '',
        questionConstraint: '',
      },
      /** 提示词配置 Tab 内区块顺序（含 rolePersona + 业务块） */
      flowSectionOrder: [...DEFAULT_PROMPT_SECTION_ORDER],
      /** 用户「增加内容」添加的模块：{ id, title, body } */
      customFlowSections: [],
      /** 固定模块标题覆盖（key → 文案），缺省用 FLOW_SECTION_META[key].title */
      flowSectionTitles: {},
      /** 固定模块标题：true 显示输入框 */
      fixedTitleShowInput: {},
      /** 自定义块标题：true 显示输入框，false 显示文本（回车/失焦后） */
      customTitleShowInput: {},
      dialogueCount: 6,
      expanded: {
        [ROLE_PERSONA_SECTION_KEY]: false,
        roleInfo: false,
        roleDesc: false,
        opening: false,
        businessBackground: false,
        businessTarget: false,
        businessProcess: false,
        // intentTag: false,
        // dialogueExample: false,
        dialogueConstraint: false,
        questionConstraint: false,
        qa: false,
        dialogue: false,
        flow: false,
        promptPreview: false,
      },
      intentTag: createDefaultIntentTagState(),
      /** 模型与语音（写入 llmsettings.voice_settings） */
      voiceCall: {
        voiceUiTab: 'preset',
        presetMode: PRESET_MODE.ENGINE,
        serviceVersion: SERVICE_VERSION.SUPREME,
        feeVersions: null,
        tripleAccess: createDefaultTripleAccess(),
        flagshipAccess: createDefaultFlagshipAccess(),
        clonedVoices: [],
        model: '',
        voiceEngine: VOICE_ENGINE.ALIYUN,
        presetVoiceId: getDefaultPresetId(VOICE_ENGINE.ALIYUN),
        speechParams: createDefaultSpeechParams(),
        ttsSpeechParams: createDefaultSpeechParams(),
        asrSpeechParams: createDefaultSpeechParams(),
        flagshipTtsSpeechParams: createDefaultSpeechParams(),
        flagshipAsrSpeechParams: createDefaultSpeechParams(),
        callControl: createDefaultCallControl(),
      },
      updateTimeStr: '—',
      publishModalVisible: false,
      publishLoading: false,
      userRole: null,
      /** 业务模块「配置指引」折叠态（仅前端 UI，文案在 FLOW_SECTION_META） */
      flowGuideExpanded: {},
    };
  },
  computed: {
    isThirdLlm() {
      return String(this.voiceCall.model || '').trim() === 'thirdllm'
    },
    isAdminRole() {
      return String(this.userRole == null ? '' : this.userRole).trim() === '1'
    },
    headerSceneCode() {
      return this.sceneId ? `${this.sceneId}` : '';
    },
    // 从 Vuex 场景模块映射需要的字段
    ...mapState('scene', {
      storeSceneId: 'sceneId',
      storeSceneName: 'sceneName',
      storeSceneRelease: 'isRelease',
      storeSceneCreateAt: 'createAt',
      storeSceneUpdateAt: 'updateAt',
      storeScene: 'scene',
    }),
    isG2Mode() {
      return isG2TripleAccessMode(this.voiceCall)
    },
    isFlagshipService() {
      return resolveServiceVersion({
        serviceVersion: this.voiceCall.serviceVersion,
        presetMode: this.voiceCall.presetMode,
      }) === SERVICE_VERSION.FLAGSHIP
    },
    isSupremeService() {
      return resolveServiceVersion({
        serviceVersion: this.voiceCall.serviceVersion,
        presetMode: this.voiceCall.presetMode,
      }) === SERVICE_VERSION.SUPREME
    },
  },
  mounted() {
    this.fetchUserRole()
    this.loadHuashuFeeVersions()
  },
  watch: {
    storeSceneId: {
      immediate: true,
      handler(id) {
        if (!id) return;
        this.sceneId = id;
      },
    },
    storeSceneName: {
      immediate: true,
      handler(name) {
        if (!name) return;
        this.llmTitle = name;
      },
    },
    storeSceneRelease: {
      immediate: true,
      handler(isRelease) {
        if (isRelease < 0) return;
        this.releaseText = isRelease === 1 ? '已发布' : '未发布';
      },
    },
    storeSceneCreateAt: {
      immediate: true,
      handler(createAt) {
        if (!createAt) {
          this.createTime = '';
          return;
        }
        this.createTime = moment(createAt * 1000).format('YYYY-MM-DD HH:mm');
      },
    },
    storeSceneUpdateAt: {
      immediate: true,
      handler(ts) {
        if (!ts) {
          this.updateTimeStr = '—';
          return;
        }
        this.updateTimeStr = moment(ts * 1000).format('YYYY-MM-DD HH:mm');
      },
    },
    storeScene: {
      immediate: true,
      handler(sceneJson) {
        if (!sceneJson || typeof sceneJson !== 'object') return;
        const llmsettings =
          sceneJson.llmsettings && typeof sceneJson.llmsettings === 'object'
            ? sceneJson.llmsettings
            : {};
        this.applyLlmToForm(llmsettings);
      },
    },
    isThirdLlm(isThirdLlm) {
      if (!isThirdLlm) return
      const callControl = this.voiceCall && this.voiceCall.callControl
      if (!callControl || typeof callControl !== 'object') return
      const raw = Number(callControl.call_timeout)
      const value = Number.isFinite(raw) ? Math.floor(raw) : 35
      this.$set(callControl, 'call_timeout', Math.min(90, Math.max(10, value)))
    },
  },
  methods: {
    fetchUserRole() {
      getUserInfoA()
        .then((res) => {
          if (!res || res.code !== 200 || !res.data) {
            this.userRole = null
            return
          }
          this.userRole = res.data.role
        })
        .catch(() => {
          this.userRole = null
        })
    },
    /**
     * 将 scene.llmsettings 写入表单；非 v2 时仅基于 llmsettings 内存迁移后展示。
     * 保存/发布只写 llmsettings，不读写 scene.settings.llm。
     */
    applyLlmToForm(llmsettings = {}) {
      const firstNode =
        this.storeScene &&
        Array.isArray(this.storeScene.scenes) &&
        this.storeScene.scenes[0] &&
        Array.isArray(this.storeScene.scenes[0].nodes)
          ? this.storeScene.scenes[0].nodes[0]
          : null
      const sceneHasThirdLlm = isExternalThirdLlmNode(firstNode, llmsettings)
      const v2 = coerceLlmsettingsForUi(llmsettings);
      const raw = v2 && typeof v2 === 'object' ? v2 : {};
      const check = validateLlmsettingsSchema(raw);
      if (!check.valid) {
        return false;
      }
      if (check.empty) {
        this.$set(this.voiceCall, 'model', sceneHasThirdLlm ? 'thirdllm' : '')
        this.promptMode = PROMPT_MODE.FREE;
        return true;
      }

      const tabs = parseLlmsettingsV2(raw);
      const promptCfg = tabs.prompt_config || {};
      const callTab = tabs.call_control_settings || {};
      const intentTab = tabs.intent_and_tags || {};

      const lsRs =
        promptCfg.role_setting && typeof promptCfg.role_setting === 'object'
          ? promptCfg.role_setting
          : null;

      if (lsRs && lsRs.nickname != null) {
        this.form.roleName = String(lsRs.nickname);
      }
      if (lsRs && Array.isArray(lsRs.identity) && lsRs.identity.length) {
        this.form.identities = lsRs.identity.map((item) => ({
          text: String((item && item.role_name) || '').trim(),
          active: !!(item && item.selection),
        }));
      }
      if (lsRs && Array.isArray(lsRs.communication_style) && lsRs.communication_style.length) {
        this.form.communicationStyles = lsRs.communication_style.map((item) => ({
          text: String((item && item.communication_style_name) || '').trim(),
          active: !!(item && item.selection),
        }));
      }
      if (lsRs && lsRs.description != null) {
        this.form.roleDesc = String(lsRs.description);
      }

      this.form.customPrompt =
        promptCfg.custom_prompt != null ? String(promptCfg.custom_prompt) : '';

      this.form.customTools =
        callTab.custom_tools != null ? String(callTab.custom_tools) : '';

      const externalApisGroup = applyExternalApisGroupFromSnapshot(callTab, this.form.customTools)
      this.form.externalApisJson = externalApisGroup.json
      this.form.externalApisForm = externalApisGroup.form

      this.form.eventTriggers = applyEventTriggersFromSnapshot(callTab.event_triggers)

      this.form.littleAssistant = applyLittleAssistantFromSettings(callTab.assistant_settings);
      this.form.customerInfoParams = applyCustomerInfoParamsFromSnapshot(
        callTab.customer_info_params,
      );

      if (Array.isArray(promptCfg.business_flow) && promptCfg.business_flow.length) {
        const biz = applyBusinessFlowFromLlmsettings(
          promptCfg.business_flow,
          this.customFlowSections,
        );
        Object.keys(biz.formPatch).forEach((k) => {
          this.form[k] = biz.formPatch[k];
        });
        this.customFlowSections = biz.customFlowSections;
        this.flowSectionTitles =
          biz.flowSectionTitles && typeof biz.flowSectionTitles === 'object'
            ? biz.flowSectionTitles
            : {};
        this.flowSectionOrder = normalizePromptSectionOrder(
          biz.flowSectionOrder,
          biz.customFlowSections,
        );
      }

      applyIntentTagFromSnapshot(intentTab, this.intentTag);

      this.applyVoiceSettingsFromLlmsettings(tabs.voice_settings || {});
      this.$set(this.voiceCall, 'model', sceneHasThirdLlm ? 'thirdllm' : '');

      const cc = callTab.call_control;
      if (cc && typeof cc === 'object') {
        if (!this.voiceCall.callControl || typeof this.voiceCall.callControl !== 'object') {
          this.$set(this.voiceCall, 'callControl', createDefaultCallControl());
        }
        applyCallControlToForm(cc, this.voiceCall.callControl, {
          isCustomPreset:
            (this.voiceCall.presetMode || PRESET_MODE.ENGINE) === PRESET_MODE.CUSTOM,
          isThirdLlm: this.isThirdLlm,
        });
      } else if (!this.voiceCall.callControl) {
        this.$set(this.voiceCall, 'callControl', createDefaultCallControl());
      }

      this.promptMode = PROMPT_MODE.FREE;
      return true;
    },
    applyLlmFromHistoryRestore(payload) {
      if (!payload || typeof payload !== 'object') {
        this.applyLlmToForm({});
        return;
      }
      const ok = this.applyLlmToForm(payload.llmsettings || {});
      if (ok) {
        this.activeMainTab = 'prompt';
      }
    },
    /** 角色信息一段文本（姓名、身份、沟通风格），与保存 prompt 中「角色信息」块一致 */
    getRoleInfoTextForPrompt() {
      const roleInfoLines = [];
      if (this.form.roleName != null && String(this.form.roleName).trim())
        roleInfoLines.push(`姓名：${String(this.form.roleName).trim()}`);
      // if (this.form.gender != null && String(this.form.gender).trim())
      //   roleInfoLines.push(`性别：${String(this.form.gender).trim()}`);
      const identityTexts = (this.form.identities || [])
        .filter((i) => {
          if (!i) return false;
          if (typeof i === 'object') return !!i.active;
          return true;
        })
        .map((i) => (i && typeof i === 'object' ? (i.text ?? i.label ?? '') : String(i)))
        .filter(Boolean);
      if (identityTexts.length)
        roleInfoLines.push(`身份：${identityTexts.join('、')}`);
      const styleTexts = (this.form.communicationStyles || [])
        .filter((c) => {
          if (!c) return false;
          if (typeof c === 'object') return !!c.active;
          return true;
        })
        .map((c) => (c && typeof c === 'object' ? (c.text ?? c.label ?? '') : String(c)))
        .filter(Boolean);
      if (styleTexts.length)
        roleInfoLines.push(`沟通风格：${styleTexts.join('、')}`);
      return roleInfoLines.join('\n');
    },
    /**
     * 克隆音色列表项归一化（持久化字段：voice_id、name、vendor、audio_file_path、audio_file_name、gender）
     * 兼容旧数据的 id / engine / clone_mode / tts_engine（不再写入 tts_vendor）
     */
    normalizeVoiceCloneEntry(x) {
      if (!x || typeof x !== 'object') return null;
      const voiceId = String(
        x.voice_id != null && String(x.voice_id).trim() !== ''
          ? x.voice_id
          : x.id != null
            ? x.id
            : '',
      ).trim();
      if (!voiceId) return null;
      const legacyEngine = x.engine != null ? String(x.engine).trim() : '';
      const legacyCloneMode = x.clone_mode != null ? String(x.clone_mode).trim() : '';
      let vendor =
        x.vendor != null && String(x.vendor).trim() !== '' ? String(x.vendor).trim() : '';
      const isTriple =
        vendor === TRIPLE_CLONE_VENDOR ||
        legacyEngine === VOICE_ENGINE.TRIPLE ||
        legacyCloneMode === 'triple';
      if (!vendor) {
        if (isTriple) {
          vendor = getCloneVendor(VOICE_ENGINE.TRIPLE);
        } else if (legacyEngine) {
          vendor = getCloneVendor(resolveVoiceEngine(legacyEngine));
        } else {
          vendor = getCloneVendor(VOICE_ENGINE.ALIYUN);
        }
      }
      const audio_file_path = String(
        x.audio_file_path != null ? x.audio_file_path : '',
      ).trim();
      const name = String(x.name != null ? x.name : '');
      const audio_file_name = String(
        x.audio_file_name != null ? x.audio_file_name : name,
      ).trim();
      const entry = {
        voice_id: voiceId,
        name,
        vendor,
        audio_file_path,
        selection: false,
      };
      if (audio_file_name) entry.audio_file_name = audio_file_name;
      if (x.gender != null && String(x.gender).trim() !== '') {
        entry.gender = String(x.gender).trim();
      }
      if (isTriple) {
        const productLine = String(
          x.product_line != null ? x.product_line : x.productLine != null ? x.productLine : '',
        ).trim();
        entry.product_line = productLine;
      }
      return entry;
    },
    /** 列表暂不支持点击选中，统一清除 selection */
    sanitizeCloneVoicesSelection(list) {
      if (!Array.isArray(list) || list.length === 0) return;
      list.forEach((x) => {
        if (x) this.$set(x, 'selection', false);
      });
    },
    /** preset_voice_id 是否为系统预设音色（非克隆返回的长 voice_id） */
    isCatalogPresetVoiceId(id, engine) {
      const s = String(id || '').trim();
      if (!s) return false;
      if (engine != null && String(engine).trim() !== '') {
        return !!getPresetCatalogEntry(s, engine);
      }
      return isPresetCatalogVoiceId(s);
    },
    /** 进入页面时拉系统费版本，再和话术 JSON 一起决定当前服务版本 */
    loadHuashuFeeVersions() {
      getSystemFeeForHuashuEditA()
        .then((res) => {
          const raw = res && res.code === 200 ? res.data : [];
          this.$set(this.voiceCall, 'feeVersions', normalizeHuashuFeeVersions(raw));
          this.applyResolvedServiceVersion();
        })
        .catch(() => {
          this.$set(this.voiceCall, 'feeVersions', []);
          this.applyResolvedServiceVersion();
        });
    },
    applyResolvedServiceVersion() {
      const next = resolveServiceVersionWithFee(this.voiceCall.feeVersions, this.latestVoiceSettings);
      this.voiceCall.serviceVersion = next;
      this.voiceCall.presetMode =
        next === SERVICE_VERSION.STANDARD ? PRESET_MODE.CUSTOM : PRESET_MODE.ENGINE;
    },
    /** 从 llmsettings.voice_settings 恢复到 voiceCall（字段名与导出 JSON 一致） */
    applyVoiceSettingsFromLlmsettings(vs) {
      if (!vs || typeof vs !== 'object') return;

      const at = vs.active_tab;
      if (at && typeof at === 'object') {
        const tab = at.tab || at.current || at.selected;
        if (tab === 'system_preset' || tab === 'preset') {
          this.voiceCall.voiceUiTab = 'preset';
        } else if (tab === 'custom_voices' || tab === 'mine') {
          this.voiceCall.voiceUiTab = 'mine';
        }
        const customVoiceRows = Array.isArray(at.custom_voices) ? at.custom_voices : [];
        if (customVoiceRows.length > 0) {
          this.voiceCall.clonedVoices = customVoiceRows
            .map((row) => this.normalizeVoiceCloneEntry(row))
            .filter(Boolean);
          this.sanitizeCloneVoicesSelection(this.voiceCall.clonedVoices);
        }
      }

      const sp = vs.system_preset;
      this.voiceCall.model = '';
      if (sp && typeof sp === 'object') {
        this.voiceCall.model = sp.model != null ? String(sp.model).trim() : '';
        if (sp.voice_engine != null && String(sp.voice_engine).trim() !== '') {
          this.voiceCall.voiceEngine = resolveVoiceEngine(sp.voice_engine);
        }
        if (sp.preset_voice_id != null && String(sp.preset_voice_id).trim() !== '') {
          this.voiceCall.presetVoiceId = String(sp.preset_voice_id);
        }
      }

      if (vs.triple_access && typeof vs.triple_access === 'object') {
        if (!this.voiceCall.tripleAccess || typeof this.voiceCall.tripleAccess !== 'object') {
          this.$set(this.voiceCall, 'tripleAccess', createDefaultTripleAccess());
        }
        applyTripleAccessToForm(vs.triple_access, this.voiceCall.tripleAccess, {
          clonedVoices: this.voiceCall.clonedVoices,
        });
      } else if (!this.voiceCall.tripleAccess) {
        this.$set(this.voiceCall, 'tripleAccess', createDefaultTripleAccess());
      }

      this.$set(this.voiceCall, 'flagshipAccess', createDefaultFlagshipAccess());
      restoreFlagshipAccess(vs.triple_access, vs.flagship_access, this.voiceCall.flagshipAccess);

      this.latestVoiceSettings = vs;
      this.applyResolvedServiceVersion();

      const pc = vs.parameter_config;
      if (pc && typeof pc === 'object') {
        applySpeechParamsFromParameterConfig(pc, this.voiceCall);
      }
    },
    buildSystemPresetPayload(vc) {
      const engineSnap = resolveVoiceEngine(vc.voiceEngine);
      let presetIdSnap = String(vc.presetVoiceId != null ? vc.presetVoiceId : '').trim();
      const isCatalog = this.isCatalogPresetVoiceId(presetIdSnap, engineSnap);
      const cloneRow = findEngineCloneVoiceById(vc.clonedVoices, presetIdSnap, engineSnap);
      if (!isCatalog && !cloneRow) {
        presetIdSnap = getDefaultPresetId(engineSnap);
      }
      const presetCatalog = isCatalog ? getPresetCatalogEntry(presetIdSnap, engineSnap) : null;
      const normalizedClone = cloneRow ? this.normalizeVoiceCloneEntry(cloneRow) : null;
      const systemPreset = {
        mode: PRESET_MODE.ENGINE,
        voice_engine: engineSnap,
        preset_voice_id: presetIdSnap,
      };
      const model = String(vc.model != null ? vc.model : '').trim();
      if (model) systemPreset.model = model;
      if (presetCatalog && presetCatalog.vendor && presetCatalog.voice_id) {
        systemPreset.vendor = presetCatalog.vendor;
        systemPreset.voice_id = presetCatalog.voice_id;
      } else if (normalizedClone) {
        systemPreset.vendor = normalizedClone.vendor;
        systemPreset.voice_id = normalizedClone.voice_id;
      }
      const audioPath =
        (presetCatalog && presetCatalog.audio_file_path) ||
        (normalizedClone && normalizedClone.audio_file_path) ||
        '';
      if (String(audioPath).trim()) {
        systemPreset.audio_file_path = String(audioPath).trim();
      }
      return systemPreset;
    },
    /** 写入 llmsettings.voice_settings（parameter_config.list 为 name/key；call_control 为蛇形字段） */
    buildVoiceSettingsSnapshot() {
      const vc = this.voiceCall;
      if (!vc.callControl || typeof vc.callControl !== 'object') {
        this.$set(vc, 'callControl', createDefaultCallControl());
      }

      const serviceVersion = resolveServiceVersion({
        serviceVersion: vc.serviceVersion,
        presetMode: vc.presetMode,
      });
      const isCustomPreset = serviceVersion === SERVICE_VERSION.STANDARD;
      const systemPreset = this.buildSystemPresetPayload(vc);
      systemPreset.selected = serviceVersion === SERVICE_VERSION.SUPREME;

      const snapshot = {
        service_version: serviceVersion,
        model_name: resolveSelectedLlmModelName({
          serviceVersion,
          presetMode: vc.presetMode,
          voiceEngine: vc.voiceEngine,
          llm: vc.tripleAccess && vc.tripleAccess.llm,
        }),
        active_tab: {
          custom_voices: (vc.clonedVoices || [])
            .map((row) => this.normalizeVoiceCloneEntry(row))
            .filter(Boolean),
        },
        system_preset: systemPreset,
        triple_access: buildTripleAccessSnapshot(vc.tripleAccess, {
          selected: isCustomPreset,
          clonedVoices: vc.clonedVoices,
          isThirdLlm: this.isThirdLlm,
        }),
        flagship_access: buildFlagshipAccessSnapshot(vc.flagshipAccess, {
          selected: serviceVersion === SERVICE_VERSION.FLAGSHIP,
        }),
        parameter_config: buildParameterConfigSnapshot({
          speechParams: vc.speechParams,
          ttsSpeechParams: vc.ttsSpeechParams,
          asrSpeechParams: vc.asrSpeechParams,
          flagshipTtsSpeechParams: vc.flagshipTtsSpeechParams,
          flagshipAsrSpeechParams: vc.flagshipAsrSpeechParams,
        }),
        call_control: buildCallControlSnapshot(vc.callControl, {
          isCustomPreset,
          isThirdLlm: this.isThirdLlm,
        }),
      };
      return snapshot;
    },
    /**
     * 聚合大模型全部配置，仅写入 scene.llmsettings（与后端/导出约定字段名）
     */
    buildLlmSettingsSnapshot() {
      const identity = (this.form.identities || [])
        .map((i) => {
          const role_name =
            i && typeof i === 'object'
              ? String((i.text ?? i.label ?? '') || '').trim()
              : String(i || '').trim();
          return {
            role_name,
            selection: !!(i && typeof i === 'object' && i.active),
          };
        })
        .filter((row) => row.role_name);

      const communication_style = (this.form.communicationStyles || [])
        .map((c) => {
          const communication_style_name =
            c && typeof c === 'object'
              ? String((c.text ?? c.label ?? '') || '').trim()
              : String(c || '').trim();
          return {
            communication_style_name,
            selection: !!(c && typeof c === 'object' && c.active),
          };
        })
        .filter((row) => row.communication_style_name);

      const business_flow = (this.flowSectionOrder || [])
        .filter((k) => k !== ROLE_PERSONA_SECTION_KEY)
        .map((k) => {
        if (isCustomFlowKey(k)) {
          const row = getCustomRow(this.customFlowSections, k);
          const title = row ? String(row.title || '').trim() || '自定义' : '自定义';
          const content = row && row.body != null ? String(row.body) : '';
          return { key: k, title, content };
        }
        return {
          key: k,
          title: getFixedSectionTitle(this.flowSectionTitles, k),
          content:
            getFlowSectionPromptValue(this.form, this.intentTag, k) != null
              ? String(getFlowSectionPromptValue(this.form, this.intentTag, k))
              : '',
        };
      });

      const intentSnapshot = buildIntentTagSnapshot(
        this.isThirdLlm ? { ...this.intentTag, mode: INTENT_TAG_MODE.FORCED } : this.intentTag,
        this.storeSceneName,
      );
      if (this.isThirdLlm) {
        intentSnapshot.intent_tags = [];
        intentSnapshot.rating_rules = [];
      }

      return buildTabbedLlmsettingsDocument({
        role_setting: {
          identity,
          communication_style,
          nickname: this.form.roleName != null ? String(this.form.roleName) : '',
          description: this.form.roleDesc != null ? String(this.form.roleDesc) : '',
        },
        business_flow,
        custom_prompt:
          this.form.customPrompt != null ? String(this.form.customPrompt) : '',
        prompt_mode: this.isThirdLlm ? PROMPT_MODE.FREE : snapshotPromptMode(this.promptMode),
        voice_settings: this.buildVoiceSettingsSnapshot(),
        intent_mode: intentSnapshot.intent_mode,
        intent_tags: intentSnapshot.intent_tags,
        extract_form: intentSnapshot.extract_form,
        rating_rules: intentSnapshot.rating_rules,
        custom_tools:
          this.form.customTools != null ? String(this.form.customTools) : '',
        ...buildExternalApisGroupSnapshot(this.form.externalApisJson, this.form.externalApisForm),
        event_triggers: buildEventTriggersSnapshot(this.form.eventTriggers),
        assistant_settings: buildLittleAssistantSnapshot(this.form.littleAssistant),
        customer_info_params: buildCustomerInfoParamsSnapshot(this.form.customerInfoParams),
      });
    },
    /** 与 pickLlmTrunkIp 共用，供 ensureThirdLlmSceneInSceneJson 写入 nodes[].trunk_ip */
    resolveLlmTrunkIp() {
      return pickLlmTrunkIp(LLM_SCENE_TRUNK_CONFIG)
    },
    /**
     * 保存/发布前保证 sceneJson.scenes[0] 含大模型节点（F00001/L00001）。
     * 旗舰版写入 thirdllm，并带上 llm_source=llm，页面据此仍显示大模型界面。
     * agentid、trunk_ip 置空。
     * 至尊版 / 标准版仍写 llm-omini，并填回话术 ID 与线路地址。
     * 模型本身已是 thirdllm 时复用 L00001：刚从普通节点切过来才清空路由字段，后端已写入的值保留。
     */
    ensureThirdLlmSceneInSceneJson(scene) {
      const next = cloneSceneJson(scene)
      const isFlagship = this.isFlagshipService
      const useThirdLlmNode = isFlagship || this.isThirdLlm
      const sid = this.sceneId != null && this.sceneId !== '' ? Number(this.sceneId) : NaN
      const filledAgentId = String(Number.isFinite(sid) ? sid : this.sceneId != null ? this.sceneId : '')
      const nameVal =
        this.storeSceneName != null && String(this.storeSceneName).trim() !== ''
          ? String(this.storeSceneName).trim()
          : '双工大模型'
      const filledTrunkIp = this.resolveLlmTrunkIp()

      if (!Array.isArray(next.scenes)) next.scenes = []

      let flow = next.scenes[0]
      if (!flow || typeof flow !== 'object') {
        flow = { id: 'F00001', name: '双工大模型', nodes: [] }
        next.scenes[0] = flow
      }
      if (flow.id == null || String(flow.id).trim() === '') flow.id = 'F00001'
      if (flow.name == null || String(flow.name).trim() === '') flow.name = '双工大模型'
      if (!Array.isArray(flow.nodes)) flow.nodes = []

      const thirdBase = {
        id: 'L00001',
        type: useThirdLlmNode ? 'thirdllm' : 'llm-omini',
        name: nameVal,
        tag: '',
        top: 200,
        left: 0,
      }
      if (!useThirdLlmNode) {
        thirdBase.agentid = filledAgentId
        thirdBase.trunk_ip = filledTrunkIp
      }
      applyLlmNodeSource(thirdBase, isFlagship)

      const nodeByIdIndex = flow.nodes.findIndex(
        (node) => node && String(node.id) === 'L00001',
      )
      const existingIndex =
        nodeByIdIndex >= 0
          ? nodeByIdIndex
          : flow.nodes.findIndex(
              (node) => node && (node.type === 'llm-omini' || node.type === 'thirdllm'),
            )
      if (existingIndex >= 0) {
        const previousNode = cloneSceneJson(flow.nodes[existingIndex])
        const existing = applyLlmNodeSource({ ...previousNode, ...thirdBase }, isFlagship)
        if (isFlagship || (this.isThirdLlm && previousNode.type !== 'thirdllm')) {
          existing.agentid = ''
          existing.trunk_ip = ''
        }
        if (useThirdLlmNode) {
          flow.nodes.splice(existingIndex, 1)
          flow.nodes = [
            existing,
            ...flow.nodes.filter((node) => !node || String(node.id) !== 'L00001'),
          ]
        } else {
          flow.nodes[existingIndex] = existing
          flow.nodes = flow.nodes.filter(
            (node, index) => index === existingIndex || !node || String(node.id) !== 'L00001',
          )
        }
      } else {
        if (isFlagship) {
          thirdBase.agentid = ''
          thirdBase.trunk_ip = ''
        }
        flow.nodes = [cloneSceneJson(thirdBase), ...flow.nodes]
      }
      return next
    },
    /**
     * 强制收取 · 信息采集有字段时：
     * 1) 创建/更新 CRM 自定义表单（已有 form_id 则 update，不再 add）
     * 2) 将表单绑定到当前话术（已绑定则不再调 scene/add）
     * 失败不阻断话术保存，仅提示。
     */
    syncCrmFormFromExtract() {
      if (!hasForcedExtractFormData(this.intentTag)) {
        return Promise.resolve()
      }
      const payload = buildCrmFormPayload(this.intentTag, this.storeSceneName)
      const applyFormId = (formId) => {
        if (formId) {
          this.$set(this.intentTag, 'extractFormId', formId)
        }
      }
      // 更新前拉取已有表单，合并推送设置，避免话术保存清空 pushApi / push_grade 等
      const withPreservedPushSettings = (nextPayload, existingFormHint) => {
        if (!nextPayload || !nextPayload.id) return Promise.resolve(nextPayload)
        return getCrmFormA({ id: nextPayload.id })
          .then((res) => {
            const data = res && res.code === 200 ? res.data : null
            const existing = data || existingFormHint
            return existing ? mergeCrmFormUpdatePayload(nextPayload, existing) : nextPayload
          })
          .catch(() =>
            existingFormHint
              ? mergeCrmFormUpdatePayload(nextPayload, existingFormHint)
              : nextPayload,
          )
      }
      const runUpdateOrAdd = (nextPayload) => {
        const request = nextPayload.id ? updateCrmFormA(nextPayload) : addCrmFormA(nextPayload)
        return request.then((res) => {
          if (!res || res.code !== 200) {
            const msg = (res && (res.msg || res.message)) || '创建采集表单失败'
            this.$Message && this.$Message.warning && this.$Message.warning(msg)
            return ''
          }
          const formId = parseCrmFormIdFromResponse(res) || nextPayload.id || ''
          applyFormId(formId)
          return formId
        })
      }

      const syncForm = () => {
        // 已有 form_id：合并推送配置后再更新
        if (payload.id) {
          return withPreservedPushSettings(payload).then((merged) => runUpdateOrAdd(merged))
        }
        // 创建前按名称查重
        return getCrmFormListPageA({
          name: payload.name,
          page: 1,
          size: 20,
        }).then((listRes) => {
          const existed = findCrmFormByNameFromListResponse(listRes, payload.name)
          const formId = existed ? toCrmFormIdNumber(existed.id) : null
          if (formId != null) {
            applyFormId(formId)
            // 已存在则不再创建，仅回填 id 并同步字段（同时保留推送设置）
            return withPreservedPushSettings(
              {
                id: formId,
                name: payload.name,
                content: payload.content,
              },
              existed,
            ).then((merged) => runUpdateOrAdd(merged))
          }
          return runUpdateOrAdd(payload)
        })
      }

      return syncForm()
        .then((formId) => this.bindCrmFormToScene(formId))
        .catch(() => {
          this.$Message &&
            this.$Message.warning &&
            this.$Message.warning('同步采集表单失败，话术仍会继续保存')
        })
    },
    /**
     * 将采集表单绑定到当前话术。
     * 已存在绑定关系时不再调用 scene/add；仅当绑定的表单 id 变化时走 scene/update。
     */
    bindCrmFormToScene(formId) {
      const sceneId = Number(this.sceneId || 0)
      const customFormId = toCrmFormIdNumber(formId || this.intentTag.extractFormId)
      if (!sceneId || customFormId == null) {
        return Promise.resolve()
      }

      return getCrmFormSceneListA({ scene_id: sceneId })
        .then((listRes) => {
          const list = parseCrmFormSceneListFromResponse(listRes)
          const existing = list.find((item) => item && item.id != null) || list[0]
          if (existing && existing.id != null) {
            // 已绑定：不再调 scene/add
            if (toCrmFormIdNumber(existing.custom_form_id) === customFormId) {
              return
            }
            // 绑定的表单变了，更新绑定关系
            return updateCrmFormSceneA({
              id: existing.id,
              scene_id: sceneId,
              custom_form_id: customFormId,
            }).then((res) => {
              if (res && res.code !== 200) {
                const msg = (res && (res.msg || res.message)) || '更新采集表单绑定失败'
                this.$Message && this.$Message.warning && this.$Message.warning(msg)
              }
            })
          }
          // 首次绑定
          return addCrmFormSceneA({
            scene_id: sceneId,
            custom_form_id: customFormId,
          }).then((res) => {
            if (res && res.code !== 200) {
              const msg = (res && (res.msg || res.message)) || '绑定采集表单失败'
              this.$Message && this.$Message.warning && this.$Message.warning(msg)
            }
          })
        })
        .catch(() => {
          this.$Message &&
            this.$Message.warning &&
            this.$Message.warning('绑定采集表单失败，话术仍会继续保存')
        })
    },
    /**
     * 【跨模块回写】大模型「振铃时长」→ 流程设置 · 一般设置「呼叫超时时长」
     * 普通模型保留该同步；thirdllm 页面不再修改外部字段。
     */
    syncGeneralCallTimeoutFromRinging(scene) {
      if (!scene || typeof scene !== 'object' || this.isThirdLlm) return scene

      const callControl =
        this.voiceCall && this.voiceCall.callControl && typeof this.voiceCall.callControl === 'object'
          ? this.voiceCall.callControl
          : null
      const raw = callControl && callControl.call_timeout != null ? Number(callControl.call_timeout) : NaN
      if (!Number.isFinite(raw) || raw < 1) return scene
      const callTimeout = Math.max(1, Math.floor(raw))

      const settings =
        scene.settings && typeof scene.settings === 'object' ? { ...scene.settings } : {}
      const general =
        settings.general && typeof settings.general === 'object' ? { ...settings.general } : {}
      general.call_timeout = callTimeout
      settings.general = general
      scene.settings = settings
      return scene
    },
    /**
     * 保存大模型配置。
     * 主路径：写入 scene.llmsettings + scenes 节点。
     */
    handleSave(options = {}) {
      const { showSuccessToast = true } = options
      const baseScene = this.storeScene;
      if (!baseScene || typeof baseScene !== 'object') {
        this.$Message && this.$Message.error && this.$Message.error('话术数据未加载，无法保存');
        return Promise.reject(new Error('no scene'));
      }

      return this.syncCrmFormFromExtract().then(() => {
        const llmsettingsPayload = this.buildLlmSettingsSnapshot();
        const newScene = this.ensureThirdLlmSceneInSceneJson({
          ...cloneSceneJson(baseScene),
          id: this.sceneId || baseScene.id,
          version: baseScene.version || '2.2',
          name: this.storeSceneName || baseScene.name,
          llmsettings: llmsettingsPayload,
        });
        this.syncGeneralCallTimeoutFromRinging(newScene);
        return this.$store
          .dispatch('scene/saveSceneRelation', {
            oldScene: baseScene,
            newScene,
          })
          .then(() => this.$store.dispatch('scene/saveScene', newScene))
          .then(() => {
            if (showSuccessToast) {
              this.$Message && this.$Message.success && this.$Message.success('保存成功');
            }
          })
          .catch(() => {
            this.$Message && this.$Message.error && this.$Message.error('保存失败，请重试');
            return Promise.reject(new Error('save failed'));
          });
      });
    },
    onClickPublish() {
      if (!this.sceneId) {
        this.$Message.error('话术未加载，无法发布');
        return;
      }
      this.publishModalVisible = true;
    },
    doPublish(applyToRunningTasks) {
      const id = Number(this.sceneId || 0);
      if (!id) return;
      this.publishLoading = true;
      // 与保存一致：先落库（含 scenes[0].nodes llm-omini 字段），再调发布接口
      this.handleSave({ showSuccessToast: false })
        .then(() =>
          publishSceneA({
            sceneId: id,
            applyAll: applyToRunningTasks ? 'yes' : 'no',
          }),
        )
        .then((res) => {
          if (res && res.code === 200) {
            this.$Message.success('发布成功');
            this.publishModalVisible = false;
            // 仅刷新发布状态等元数据（getSceneDetail 不会覆盖 store 中的 sceneJson）
            return this.$store.dispatch('scene/getSceneDetail');
          }
          this.$Message.error((res && res.msg) || '发布失败');
          return Promise.reject(new Error('publish failed'));
        })
        .catch((err) => {
          if (err && err.message === 'publish failed') {
            return;
          }
          if (err && err.message === 'save failed') {
            return;
          }
          this.$Message.error('发布失败');
        })
        .finally(() => {
          this.publishLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.llm-config-page {
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: #f5f6f8;
  padding: 0 24px 16px;
}

/*
 * 与下方双栏同宽并整体居中：1200（主栏 max-width）+ 12（gap）+ 400（预览 max）
 * 勿对 shell 使用 fit-content：Tab 仅渲染当前 pane 时内在最小宽度变小，整体会被挤窄。
 */
.llm-layout-shell {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  max-width: 1612px;
  margin: 0 auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.page-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 16px;
  padding: 14px 20px;
  background: #fff;
  margin: 0 0 12px;
  border-radius: 0;
  border-bottom: 1px solid #e8e8e8;
  .header-left {
    flex: 1;
    min-width: 0;
  }
  .header-left-inner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
    row-gap: 8px;
  }
  .page-title {
    font-size: 20px;
    font-weight: 600;
    margin: 0;
    color: #1a1a1a;
  }
  .header-scene-code {
    font-size: 13px;
    color: #8c8c8c;
    line-height: 1.4;
  }
  .header-status-pill {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 10px;
    font-size: 12px;
    line-height: 1.5;
    color: #595959;
    background: #e8edf3;
    &.is-published {
      color: #389e0d;
      background: #f6ffed;
    }
  }
  .header-meta {
    font-size: 12px;
    color: #8c8c8c;
    line-height: 1.4;
  }
  .header-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;
    flex-shrink: 0;
  }
  .header-btn-publish {
    border: none;
    color: #fff;
    background: #1fb486;
    padding: 0 18px;
    height: 34px;
    border-radius: 4px;
    font-size: 14px;
    &:hover {
      background: #17a077;
      color: #fff;
    }
  }
  .header-btn-save {
    border: none;
    color: #fff;
    background: #0066a1;
    padding: 0 18px;
    height: 34px;
    border-radius: 4px;
    font-size: 14px;
    &:hover {
      background: #005286;
      color: #fff;
    }
  }
}

.publish-modal-tip {
  margin: 0 0 16px;
  color: #595959;
  font-size: 14px;
}
.publish-modal-btns {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.page-body-split {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  gap: 12px;
  align-items: stretch;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

/* 占满顶栏下方剩余视口高度，高度由 flex 约束而非随内容撑开 */
.page-body-main {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-body-main-inner {
  flex: 1 1 auto;
  width: 100%;
  max-width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e8f0;
}

/* 与左侧主栏同高：行向 stretch + aside 内 flex 列，iframe 在 LlmVoiceTest 内 flex:1 撑满 */
.page-body-aside {
  align-self: stretch;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: min(400px, 35vw);
  min-width: 280px;
  box-sizing: border-box;
}

@media (max-width: 1100px) {
  .llm-layout-shell {
    max-width: none;
  }
  .llm-config-page {
    height: 100vh;
    max-height: 100vh;
  }
  .page-body-split {
    flex-direction: column;
  }
  .page-body-main {
    width: 100%;
    max-width: 100%;
    flex: 1 1 auto;
    min-height: 0;
  }
  .page-body-main-inner {
    max-width: 100%;
  }
  .page-body-aside {
    position: static;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    /* 纵向堆叠时给 iframe 保底高度，避免无明确高度时塌陷 */
    min-height: min(560px, calc(100vh - 120px));
  }
}

/* 只作用于顶层 Tabs 的导航条，勿用 ::v-deep .ivu-tabs-bar 匹配内层（音色页里「系统预设/三段式接入」），否则会像多出一排主导航 */
.llm-main-tabs {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  height: 100%;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ::v-deep > .ivu-tabs-content {
    flex: 1 1 auto;
    min-height: 0;
    height: 0;
    overflow: hidden;
    background: #f0f3f9;
  }

  ::v-deep > .ivu-tabs-content > .ivu-tabs-tabpane {
    height: 100%;
    overflow: hidden;
  }

  ::v-deep > .ivu-tabs-bar {
    flex-shrink: 0;
    margin-bottom: 0;
    padding: 5px 0;
    border-bottom: 1px solid #e2e8f0;
    background: #f8f9fc;
  }

  ::v-deep > .ivu-tabs-bar .ivu-tabs-nav-container {
    margin-bottom: 0;
  }

  ::v-deep > .ivu-tabs-bar .ivu-tabs-nav-wrap {
    margin-bottom: 0;
  }

  ::v-deep > .ivu-tabs-bar .ivu-tabs-ink-bar {
    display: none;
  }

  ::v-deep > .ivu-tabs-bar .ivu-tabs-nav {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  ::v-deep > .ivu-tabs-bar .ivu-tabs-nav .ivu-tabs-tab {
    margin: 0;
    padding: 0 16px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #475569;
    font-size: 14px;
    line-height: 30px;
    transition: background 0.2s ease, color 0.2s ease;

    &:hover:not(.ivu-tabs-tab-active) {
      background: #eef1f6;
      color: #262626;
    }

    &.ivu-tabs-tab-active,
    &.ivu-tabs-tab-active:hover {
      background: #5668f0;
      color: #fff;
      font-weight: 500;
    }
  }

  /*
   * 保持主导航 Tabs 关闭内置 animated，以免多 TabPane 占位撑高。
   * View Design 的 TabPane 用内联 visibility 切换，无 tabpane-active 类名；
   * 对 visibility:visible 的面板做淡入 + 略上移，替代横向滑动。
   */
  ::v-deep > .ivu-tabs-content > .ivu-tabs-tabpane[style*='visibility: visible'],
  ::v-deep > .ivu-tabs-content > .ivu-tabs-tabpane[style*='visibility:visible'] {
    animation: llm-main-tab-pane-in 0.42s cubic-bezier(0.33, 1, 0.68, 1) both;
  }
}

/* Tab 内容区：高度与可视区域对齐，仅在此容器内滚动；滚动条默认隐藏，悬停时显示 */
.llm-tab-pane-scroll {
  height: 100%;
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
  padding: 16px 16px 16px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &:hover {
    scrollbar-color: rgba(0, 0, 0, 0.28) transparent;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 4px;
  }

  &:hover::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.22);
  }
}

@keyframes llm-main-tab-pane-in {
  from {
    opacity: 0;
    transform: translateY(7px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .llm-main-tabs ::v-deep > .ivu-tabs-content > .ivu-tabs-tabpane[style*='visibility: visible'],
  .llm-main-tabs ::v-deep > .ivu-tabs-content > .ivu-tabs-tabpane[style*='visibility:visible'] {
    animation: none;
  }
}
</style>
